import { useEffect, useState, useCallback } from "react";
import {
  Users, Plus, Pencil, Trash2, Loader2, Calendar, CalendarClock, CalendarOff,
  UploadCloud, Check, Star, Clock, Save, UserCog, Plug, KeyRound, ShieldCheck, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";

const DOW = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const defaultHours = () => Object.fromEntries(DOW.map(([d]) => [d, { open: "09:00", close: "17:00", closed: d === "sat" || d === "sun" }]));
const STATUS = { available: "bg-emerald-100 text-emerald-700", busy: "bg-amber-100 text-amber-700", on_leave: "bg-violet-100 text-violet-700", disabled: "bg-slate-200 text-slate-500" };
const CALENDARS = [["operamind", "OperaMind Calendar"], ["google_calendar", "Google Calendar"], ["outlook_calendar", "Outlook Calendar"]];

const empty = () => ({
  name: "", title: "", email: "", phone: "", bio: "", languages: [], specialisations: [],
  years_experience: "", working_hours: defaultHours(), break_times: [], leave_dates: [],
  buffer_minutes: 0, calendar_provider: "operamind", calendar_connected: true,
  status: "available", enabled: true, display_order: 0,
});

const csv = (arr) => (arr || []).join(", ");
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function Staff() {
  const [rows, setRows] = useState([]);
  const [multi, setMulti] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ staff_mode: "auto", permissions: {} });
  const [savingSettings, setSavingSettings] = useState(false);
  const [loginFor, setLoginFor] = useState(null);   // staff object whose login is being managed
  const [loginPw, setLoginPw] = useState("");
  const [loginPerms, setLoginPerms] = useState({ view_all_appointments: false, edit_appointments: false, manage_availability: true, edit_profile: true });
  const [loginBusy, setLoginBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([api.get("/staff-dashboard"), api.get("/booking-settings")]);
      setRows(d.data.staff || []); setMulti(d.data.multi_staff);
      setSettings(s.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ ...empty(), ...s, working_hours: s.working_hours && Object.keys(s.working_hours).length ? s.working_hours : defaultHours(),
      languages: s.languages || [], specialisations: s.specialisations || [], years_experience: s.years_experience ?? "" });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHour = (d, k, v) => setForm((f) => ({ ...f, working_hours: { ...f.working_hours, [d]: { ...f.working_hours[d], [k]: v } } }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Staff name is required");
    setSaving(true);
    const payload = { ...form, years_experience: form.years_experience === "" ? null : Number(form.years_experience), buffer_minutes: Number(form.buffer_minutes) || 0 };
    try {
      const res = editing ? await api.patch(`/staff/${editing}`, payload) : await api.post("/staff", payload);
      const sid = editing || res.data.id;
      if (form._photoFile && sid) {
        const fd = new FormData(); fd.append("file", form._photoFile);
        await api.post(`/staff/${sid}/photo`, fd);
      }
      toast.success(editing ? "Staff member updated" : "Staff member added");
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save staff member"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this staff member? Their assignments will be removed.")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Staff member deleted"); await load(); }
    catch { toast.error("Could not delete"); }
  };
  const toggleEnabled = async (s) => {
    try { await api.patch(`/staff/${s.id}`, { enabled: !s.enabled }); await load(); }
    catch { toast.error("Could not update"); }
  };
  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put("/booking-settings", settings); toast.success("Booking settings saved"); }
    catch { toast.error("Could not save settings"); }
    finally { setSavingSettings(false); }
  };
  const setPerm = (k, v) => setSettings((s) => ({ ...s, permissions: { ...s.permissions, [k]: v } }));

  const PERM_META = [
    ["view_all_appointments", "View all bookings", "Otherwise they only see their own appointments"],
    ["edit_appointments", "Edit appointments", "Mark complete, cancel or reschedule"],
    ["manage_availability", "Manage their availability", "Working hours, breaks and leave"],
    ["edit_profile", "Edit their own profile", "Bio, specialisations, languages"],
  ];
  const randomPw = () => Math.random().toString(36).slice(2, 6) + Math.floor(1000 + Math.random() * 9000);
  const openLogin = (s) => {
    setLoginFor(s);
    setLoginPw(s.login_enabled ? "" : randomPw());
    setLoginPerms({ view_all_appointments: false, edit_appointments: false, manage_availability: true, edit_profile: true, ...(s.permissions || {}) });
  };
  const saveLogin = async () => {
    if (!loginFor) return;
    if (!loginFor.login_enabled && loginPw.trim().length < 6) return toast.error("Temporary password must be at least 6 characters");
    setLoginBusy(true);
    try {
      if (loginFor.login_enabled) {
        // update permissions only (password unchanged)
        await api.patch(`/staff/${loginFor.id}/permissions`, { permissions: loginPerms });
        toast.success("Permissions updated");
      } else {
        await api.post(`/staff/${loginFor.id}/login`, { password: loginPw, permissions: loginPerms });
        toast.success(`Login enabled for ${loginFor.name}`);
      }
      setLoginFor(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save login"); }
    finally { setLoginBusy(false); }
  };
  const resetLoginPw = async () => {
    const pw = randomPw();
    setLoginBusy(true);
    try {
      await api.post(`/staff/${loginFor.id}/login/reset-password`, { password: pw });
      setLoginPw(pw);
      toast.success("New temporary password generated — share it with your staff member");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset password"); }
    finally { setLoginBusy(false); }
  };
  const disableLogin = async () => {
    if (!window.confirm(`Disable ${loginFor.name}'s login? They will no longer be able to sign in.`)) return;
    setLoginBusy(true);
    try { await api.post(`/staff/${loginFor.id}/login/disable`); toast.success("Login disabled"); setLoginFor(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not disable"); }
    finally { setLoginBusy(false); }
  };
  const setLp = (k, v) => setLoginPerms((p) => ({ ...p, [k]: v }));

  const addBreak = () => set("break_times", [...form.break_times, { day: "", start: "12:00", end: "13:00" }]);
  const addLeave = () => set("leave_dates", [...form.leave_dates, { start: "", end: "", reason: "" }]);
  const photoUrl = (s) => (s.photo_download ? `${API}${s.photo_download}?auth=${getToken()}` : "");

  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading staff…</div>;

  return (
    <div data-testid="staff-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Users className="h-7 w-7 text-blue-600" /> Staff</h1>
          <p className="text-slate-500 mt-1">Manage your team, availability and calendars. {multi ? <span className="text-emerald-600 font-medium">Multi-staff booking is active.</span> : "Add a second member to activate multi-staff booking."}</p>
        </div>
        <Button data-testid="add-staff-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700 gap-2"><Plus className="h-4 w-4" /> Add staff</Button>
      </div>

      {/* Booking settings */}
      <div data-testid="booking-settings" className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2"><UserCog className="h-4 w-4 text-blue-600" /> Booking settings</h2>
        <p className="text-xs text-slate-500 mt-1 mb-3">How should the AI assign staff when a customer books?</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[["auto", "Automatically assign", "Books the first available qualified staff member."],
            ["customer_choose", "Customer chooses", "AI asks: \"Do you have a preferred member of staff?\""],
            ["always_ask", "Always ask", "AI always asks for a preferred staff member first."]].map(([val, title, desc]) => (
            <button key={val} data-testid={`booking-mode-${val}`} onClick={() => setSettings((s) => ({ ...s, staff_mode: val }))}
              className={`text-left rounded-xl border p-3 transition-colors ${settings.staff_mode === val ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}>
              <div className="flex items-center gap-2 font-medium text-sm text-slate-900">{settings.staff_mode === val && <Check className="h-4 w-4 text-blue-600" />}{title}</div>
              <p className="text-xs text-slate-500 mt-1">{desc}</p>
            </button>
          ))}
        </div>
        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-blue-600" /> Each staff member's login access and permissions are managed individually — use the <b className="text-slate-700">Login</b> button on their card below.</p>
        </div>
        <Button data-testid="save-booking-settings" onClick={saveSettings} disabled={savingSettings} className="mt-4 bg-blue-600 hover:bg-blue-700 gap-2">
          {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
        </Button>
      </div>

      {/* Staff list */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400 text-sm sm:col-span-2 lg:col-span-3">No staff yet. Add your first team member to get started.</div>}
        {rows.map((s) => (
          <div key={s.id} data-testid={`staff-card-${s.id}`} className={`rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:shadow-md ${!s.enabled ? "opacity-60" : ""}`}>
            <div className="flex items-start gap-3">
              {s.photo_download
                ? <img src={photoUrl(s)} alt={s.name} className="h-14 w-14 rounded-full object-cover shrink-0" />
                : <div className="h-14 w-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg shrink-0">{s.name.slice(0, 1).toUpperCase()}</div>}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900 truncate">{s.name}</p>
                <p className="text-sm text-slate-500 truncate">{s.title || "—"}</p>
                <span className={`inline-block mt-1 rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS[s.status] || STATUS.available}`}>{(s.status || "available").replace("_", " ")}</span>
              </div>
            </div>
            {s.specialisations?.length > 0 && <p className="mt-3 text-xs text-slate-500 flex items-center gap-1"><Star className="h-3 w-3 text-amber-400" /> {s.specialisations.join(", ")}</p>}
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 p-2"><p className="text-lg font-bold text-slate-900">{s.today_count}</p><p className="text-[10px] text-slate-400">Today</p></div>
              <div className="rounded-lg bg-slate-50 p-2"><p className="text-lg font-bold text-slate-900">{s.upcoming_count}</p><p className="text-[10px] text-slate-400">Upcoming</p></div>
              <div className="rounded-lg bg-slate-50 p-2"><p className="text-lg font-bold text-slate-900">{s.total_bookings}</p><p className="text-[10px] text-slate-400">Total</p></div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center gap-1"><Plug className={`h-3.5 w-3.5 ${s.calendar_synced ? "text-emerald-500" : "text-slate-300"}`} /> {CALENDARS.find((c) => c[0] === s.calendar_provider)?.[1] || "Calendar"}</span>
              {s.next_leave && <span className="flex items-center gap-1 text-violet-500"><CalendarOff className="h-3.5 w-3.5" /> {s.next_leave.start}</span>}
            </div>
            <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3">
              <Button data-testid={`staff-edit-${s.id}`} variant="ghost" size="sm" onClick={() => openEdit(s)} className="gap-1 text-slate-600"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
              <Button data-testid={`staff-login-${s.id}`} variant="ghost" size="sm" onClick={() => openLogin(s)} className={`gap-1 ${s.login_enabled ? "text-emerald-600" : "text-slate-600"}`}>
                <KeyRound className="h-3.5 w-3.5" /> {s.login_enabled ? "Login on" : "Login"}
              </Button>
              <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-auto"><Switch data-testid={`staff-toggle-${s.id}`} checked={s.enabled} onCheckedChange={() => toggleEnabled(s)} /> {s.enabled ? "Active" : "Off"}</label>
              <Button data-testid={`staff-delete-${s.id}`} variant="ghost" size="icon" onClick={() => del(s.id)} className="text-slate-400 hover:text-red-500 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit staff member" : "Add staff member"}</DialogTitle>
            <DialogDescription>Set this team member's profile, working hours, breaks, leave and calendar. The AI uses this to book and recommend them.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              {form._photoPreview || (editing && form.photo_download)
                ? <img src={form._photoPreview || `${API}${form.photo_download}?auth=${getToken()}`} alt="" className="h-16 w-16 rounded-full object-cover" />
                : <div className="h-16 w-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xl">{(form.name || "?").slice(0, 1).toUpperCase()}</div>}
              <label className="cursor-pointer text-sm text-blue-600 font-medium flex items-center gap-1.5">
                <UploadCloud className="h-4 w-4" /> Upload photo
                <input data-testid="staff-photo-input" type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) { set("_photoFile", f); set("_photoPreview", URL.createObjectURL(f)); } }} />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full name *"><Input data-testid="staff-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
              <Field label="Job title"><Input data-testid="staff-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
              <Field label="Email"><Input data-testid="staff-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Phone (optional)"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
              <Field label="Languages spoken (comma separated)"><Input data-testid="staff-languages" value={csv(form.languages)} onChange={(e) => set("languages", toArr(e.target.value))} placeholder="English, Spanish" /></Field>
              <Field label="Specialisations (comma separated)"><Input value={csv(form.specialisations)} onChange={(e) => set("specialisations", toArr(e.target.value))} placeholder="Fades, Colour" /></Field>
              <Field label="Years of experience (optional)"><Input type="number" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} /></Field>
              <Field label="Buffer between appts (mins)"><Input type="number" value={form.buffer_minutes} onChange={(e) => set("buffer_minutes", e.target.value)} /></Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="staff-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{["available", "busy", "on_leave", "disabled"].map((v) => <SelectItem key={v} value={v} className="capitalize">{v.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Display order"><Input type="number" value={form.display_order} onChange={(e) => set("display_order", Number(e.target.value) || 0)} /></Field>
            </div>
            <Field label="Biography (optional)"><Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={2} placeholder="A short bio the AI can use to introduce them." /></Field>

            {/* Working hours */}
            <div>
              <Label className="text-xs flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Working hours</Label>
              <div className="mt-2 space-y-1.5">
                {DOW.map(([d, lbl]) => {
                  const h = form.working_hours[d] || { open: "09:00", close: "17:00", closed: true };
                  return (
                    <div key={d} className="flex items-center gap-2 text-sm">
                      <span className="w-10 text-slate-500">{lbl}</span>
                      <Switch data-testid={`hours-${d}`} checked={!h.closed} onCheckedChange={(v) => setHour(d, "closed", !v)} />
                      {h.closed ? <span className="text-slate-400 text-xs">Closed</span> : (
                        <>
                          <Input type="time" value={h.open} onChange={(e) => setHour(d, "open", e.target.value)} className="h-8 w-28" />
                          <span className="text-slate-400">–</span>
                          <Input type="time" value={h.close} onChange={(e) => setHour(d, "close", e.target.value)} className="h-8 w-28" />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Breaks */}
            <div>
              <div className="flex items-center justify-between"><Label className="text-xs flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Break times</Label>
                <Button variant="ghost" size="sm" onClick={addBreak} className="text-blue-600 gap-1 h-7"><Plus className="h-3.5 w-3.5" /> Add break</Button></div>
              {form.break_times.map((b, i) => (
                <div key={i} className="flex items-center gap-2 mt-1.5">
                  <Select value={b.day || "all"} onValueChange={(v) => set("break_times", form.break_times.map((x, j) => j === i ? { ...x, day: v === "all" ? "" : v } : x))}>
                    <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Every day</SelectItem>{DOW.map(([d, l]) => <SelectItem key={d} value={d}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="time" value={b.start} onChange={(e) => set("break_times", form.break_times.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} className="h-8 w-28" />
                  <span className="text-slate-400">–</span>
                  <Input type="time" value={b.end} onChange={(e) => set("break_times", form.break_times.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} className="h-8 w-28" />
                  <Button variant="ghost" size="icon" onClick={() => set("break_times", form.break_times.filter((_, j) => j !== i))} className="h-8 w-8 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>

            {/* Leave */}
            <div>
              <div className="flex items-center justify-between"><Label className="text-xs flex items-center gap-1"><CalendarOff className="h-3.5 w-3.5" /> Holiday / leave</Label>
                <Button variant="ghost" size="sm" onClick={addLeave} className="text-blue-600 gap-1 h-7"><Plus className="h-3.5 w-3.5" /> Add leave</Button></div>
              {form.leave_dates.map((l, i) => (
                <div key={i} className="flex items-center gap-2 mt-1.5">
                  <Input type="date" value={l.start} onChange={(e) => set("leave_dates", form.leave_dates.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} className="h-8" />
                  <span className="text-slate-400">→</span>
                  <Input type="date" value={l.end} onChange={(e) => set("leave_dates", form.leave_dates.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} className="h-8" />
                  <Input value={l.reason} onChange={(e) => set("leave_dates", form.leave_dates.map((x, j) => j === i ? { ...x, reason: e.target.value } : x))} placeholder="Reason" className="h-8" />
                  <Button variant="ghost" size="icon" onClick={() => set("leave_dates", form.leave_dates.filter((_, j) => j !== i))} className="h-8 w-8 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>

            {/* Calendar */}
            <div>
              <Label className="text-xs flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Calendar integration</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CALENDARS.map(([val, label]) => (
                  <button key={val} type="button" data-testid={`cal-${val}`}
                    onClick={() => { set("calendar_provider", val); set("calendar_connected", true); if (val !== "operamind") toast.info(`${label} will sync once OAuth is connected. Using OperaMind Calendar meanwhile.`); }}
                    className={`rounded-lg border px-3 py-2 text-sm transition-colors ${form.calendar_provider === val ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-300"}`}>
                    {form.calendar_provider === val && <Check className="h-3.5 w-3.5 inline mr-1" />}{label}{val !== "operamind" && <span className="text-[10px] text-slate-400 ml-1">(soon)</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-staff-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {editing ? "Save changes" : "Add staff member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff login management dialog */}
      <Dialog open={!!loginFor} onOpenChange={(o) => !o && setLoginFor(null)}>
        <DialogContent className="max-w-lg" data-testid="staff-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-5 w-5 text-blue-600" /> {loginFor?.name} — Login & Access</DialogTitle>
            <DialogDescription>
              {loginFor?.login_enabled
                ? "This staff member has a login. Update what they can do, reset their password, or disable access."
                : "Give this staff member their own login. Set a temporary password — they'll be asked to change it on first sign-in."}
            </DialogDescription>
          </DialogHeader>

          {loginFor && !loginFor.email && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              Add an email address for this staff member first (Edit → Email), then enable their login.
            </div>
          )}

          {loginFor && loginFor.email && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">Sign-in email: <b className="text-slate-900">{loginFor.login_email || loginFor.email}</b> <span className="text-slate-400">(they sign in at the normal login page)</span></div>

              {!loginFor.login_enabled && (
                <div>
                  <Label className="text-xs text-slate-500">Temporary password</Label>
                  <div className="flex gap-2 mt-1">
                    <Input data-testid="login-temp-password" value={loginPw} onChange={(e) => setLoginPw(e.target.value)} placeholder="At least 6 characters" />
                    <Button type="button" variant="outline" onClick={() => setLoginPw(randomPw())} className="shrink-0 gap-1"><KeyRound className="h-3.5 w-3.5" /> Generate</Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => { navigator.clipboard?.writeText(loginPw); toast.success("Copied"); }} className="shrink-0"><Copy className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">What can they do?</p>
                <div className="space-y-2">
                  {PERM_META.map(([k, label, desc]) => (
                    <label key={k} className="flex items-start gap-3 rounded-lg border border-slate-200 p-2.5 cursor-pointer">
                      <Switch data-testid={`login-perm-${k}`} checked={!!loginPerms[k]} onCheckedChange={(v) => setLp(k, v)} className="mt-0.5" />
                      <span><span className="text-sm font-medium text-slate-800">{label}</span><span className="block text-xs text-slate-400">{desc}</span></span>
                    </label>
                  ))}
                </div>
              </div>

              {loginFor.login_enabled && (
                <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
                  <Button data-testid="login-reset-pw" type="button" variant="outline" onClick={resetLoginPw} disabled={loginBusy} className="gap-1.5"><KeyRound className="h-3.5 w-3.5" /> Reset password</Button>
                  <Button data-testid="login-disable" type="button" variant="ghost" onClick={disableLogin} disabled={loginBusy} className="text-red-600 hover:bg-red-50 gap-1.5 ml-auto">Disable login</Button>
                </div>
              )}
              {loginFor.login_enabled && loginPw && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5 text-sm text-blue-800">New temporary password: <b>{loginPw}</b> — share it securely. They'll change it on next sign-in.</div>
              )}
            </div>
          )}

          {loginFor && loginFor.email && (
            <DialogFooter>
              <Button data-testid="save-staff-login" onClick={saveLogin} disabled={loginBusy} className="bg-blue-600 hover:bg-blue-700 gap-2">
                {loginBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {loginFor.login_enabled ? "Save permissions" : "Enable login"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>
);
